/**
 * Stripe Billing Helpers
 *
 * Data transform functions to build Stripe billing details from form/data inputs.
 * Maps application data structures to Stripe's PaymentMethod.BillingDetails format.
 */

import type { StripeBillingDetails } from '@chefami-monorepo/types';

/**
 * Build minimal billing details (email only).
 *
 * Used for: Gift card purchases where only sender email is provided.
 *
 * @param email - Sender's email address
 * @returns Billing details with email only
 */
export function buildMinimalBillingDetails(
  email: string,
): StripeBillingDetails {
  return {
    email: email.trim(),
  };
}

/**
 * Build full billing details with complete address information.
 *
 * Used for: New subscriptions and payment method updates requiring full billing address.
 *
 * @param params - Form data containing name, email, phone, and address
 * @returns Billing details ready for Stripe payment intent/setup intent
 */
export function buildFullBillingDetails(params: {
  name: string;
  email: string;
  phone: string;
  address: {
    line1: string;
    city: string;
    state: string;
    zipCode: string;
  };
}): StripeBillingDetails {
  return {
    name: params.name,
    email: params.email.trim(),
    phone: params.phone.trim(),
    address: {
      line1: params.address.line1,
      city: params.address.city,
      state: params.address.state,
      postal_code: params.address.zipCode,
      country: 'US',
    },
  };
}

/**
 * Build billing details for card updates from existing account data.
 *
 * Used for: Payment method updates where data comes from stored account information.
 *
 * @param params - Account data with card name, email, phone, and address
 * @returns Billing details ready for Stripe setup intent
 */
export function buildUpdateCardBillingDetails(params: {
  nameOnCard: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
}): StripeBillingDetails {
  return {
    name: params.nameOnCard,
    email: params.email,
    phone: params.phone,
    address: {
      line1: params.street,
      city: params.city,
      state: params.state,
      postal_code: params.zipcode,
      country: 'US',
    },
  };
}
